import { useMemo } from "react";
import { Card } from "./ui/card";
import { Smartphone, Wifi, Battery, Signal, Search } from "lucide-react";

/**
 * Live "Install Preview" — two phone mockups (iOS + Android) that render the
 * uploaded app icon, site name, and theme color exactly as they'd appear when
 * a user installs the PWA to their home screen.
 *
 * Reads from AdminSettings form state so it updates as the admin types.
 */
export default function InstallPreviewCard({ siteName, tagline, faviconUrl, primaryColor = "#0D9488" }) {
  const displayName = (siteName || "Your Site").trim();
  const shortName = useMemo(() => {
    // iOS truncates to ~11 chars, Android to ~14. Show a realistic snip.
    return displayName.length > 12 ? displayName.slice(0, 11) + "…" : displayName;
  }, [displayName]);
  const androidLabel = useMemo(() => {
    return displayName.length > 14 ? displayName.slice(0, 13) + "…" : displayName;
  }, [displayName]);

  return (
    <Card className="bg-white border border-slate-200 rounded-none p-6" data-testid="install-preview-card">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-8 h-8 flex items-center justify-center" style={{ background: primaryColor }}>
          <Smartphone className="w-4 h-4 text-black" />
        </div>
        <div>
          <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-slate-500">// PWA</div>
          <div className="font-display font-black uppercase tracking-tight text-xl">Install Preview</div>
        </div>
        <div className="ml-auto text-[10px] font-mono-num uppercase tracking-widest text-slate-500 hidden sm:block">
          Live · updates as you edit
        </div>
      </div>
      <p className="text-slate-600 text-sm mb-6 max-w-2xl leading-relaxed">
        This is how your app icon, name, and theme color will look after a user taps <strong className="text-slate-900">Add to Home Screen</strong> (iOS) or <strong className="text-slate-900">Install app</strong> (Android). Upload a favicon above to update both mockups.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 justify-items-center">
        <IPhonePreview icon={faviconUrl} label={shortName} themeColor={primaryColor} tagline={tagline} />
        <AndroidPreview icon={faviconUrl} label={androidLabel} themeColor={primaryColor} tagline={tagline} />
      </div>
    </Card>
  );
}

/* ---------------- iOS mockup ---------------- */

function IPhonePreview({ icon, label, themeColor }) {
  return (
    <div className="flex flex-col items-center gap-3">
      <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-slate-500">// iOS · Home Screen</div>
      <div
        className="relative w-56 h-[420px] rounded-[2.75rem] p-1.5 shadow-2xl"
        style={{ background: "#1a1a1c", boxShadow: "0 25px 60px -20px rgba(0,0,0,0.6), 0 0 0 1px #2a2a2e inset" }}
        data-testid="install-preview-ios"
      >
        {/* Screen */}
        <div
          className="relative w-full h-full rounded-[2.25rem] overflow-hidden"
          style={{
            background: `linear-gradient(160deg, ${themeColor}22 0%, #0a0a0b 45%, #0a0a0b 100%)`,
          }}
        >
          {/* Dynamic Island */}
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-6 bg-black rounded-full z-10" />
          {/* Status bar */}
          <div className="absolute top-3 left-4 right-4 flex justify-between text-white text-[10px] font-mono-num tracking-wide pointer-events-none">
            <span>9:41</span>
            <div className="flex items-center gap-1">
              <Signal className="w-2.5 h-2.5" />
              <Wifi className="w-2.5 h-2.5" />
              <Battery className="w-3 h-3" />
            </div>
          </div>
          {/* Home screen icon grid */}
          <div className="absolute inset-0 pt-14 pb-8 px-6">
            <div className="grid grid-cols-4 gap-x-3 gap-y-4">
              <AppIconIOS icon={icon} label={label} themeColor={themeColor} highlight />
              <FakeIconIOS name="Photos" hue="#0EA5E9" glyph="◈" />
              <FakeIconIOS name="Music" hue="#EC4899" glyph="♪" />
              <FakeIconIOS name="Health" hue="#EF4444" glyph="♥" />
              <FakeIconIOS name="Mail" hue="#3B82F6" glyph="✉" />
              <FakeIconIOS name="Maps" hue="#22C55E" glyph="◎" />
              <FakeIconIOS name="Clock" hue="#0a0a0b" glyph="◷" />
              <FakeIconIOS name="Notes" hue="#F59E0B" glyph="≣" />
            </div>
          </div>
          {/* Dock */}
          <div className="absolute bottom-2 left-2 right-2 h-14 rounded-2xl bg-white/10 backdrop-blur-md border border-white/10 flex items-center justify-around px-3">
            <div className="w-8 h-8 rounded-lg bg-[#0EA5E9]" />
            <div className="w-8 h-8 rounded-lg bg-[#22C55E]" />
            <div className="w-8 h-8 rounded-lg bg-[#F59E0B]" />
            <div className="w-8 h-8 rounded-lg bg-[#8B5CF6]" />
          </div>
          {/* Home indicator */}
          <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-24 h-1 bg-white/40 rounded-full" />
        </div>
      </div>
    </div>
  );
}

function AppIconIOS({ icon, label, themeColor, highlight }) {
  return (
    <div className={`flex flex-col items-center gap-1 ${highlight ? "" : ""}`}>
      <div
        className="w-11 h-11 rounded-[13px] overflow-hidden flex items-center justify-center relative"
        style={{ background: themeColor, boxShadow: highlight ? `0 0 0 2px #fff, 0 0 0 4px ${themeColor}` : "none" }}
      >
        {icon ? (
          <img src={icon} alt="" className="w-full h-full object-cover" />
        ) : (
          <span className="text-black font-black text-lg font-display">
            {(label || "?").slice(0, 1).toUpperCase()}
          </span>
        )}
      </div>
      <div className="text-[9px] text-white/90 max-w-[52px] truncate text-center leading-tight" data-testid="install-preview-ios-label">
        {label}
      </div>
    </div>
  );
}

function FakeIconIOS({ name, hue, glyph }) {
  return (
    <div className="flex flex-col items-center gap-1 opacity-45">
      <div
        className="w-11 h-11 rounded-[13px] flex items-center justify-center text-white text-lg"
        style={{ background: hue }}
      >
        <span>{glyph}</span>
      </div>
      <div className="text-[9px] text-white/70 max-w-[52px] truncate text-center leading-tight">{name}</div>
    </div>
  );
}

/* ---------------- Android mockup ---------------- */

function AndroidPreview({ icon, label, themeColor }) {
  return (
    <div className="flex flex-col items-center gap-3">
      <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-slate-500">// Android · Home Screen</div>
      <div
        className="relative w-56 h-[420px] rounded-[2rem] p-1.5 shadow-2xl"
        style={{ background: "#1a1a1c", boxShadow: "0 25px 60px -20px rgba(0,0,0,0.6), 0 0 0 1px #2a2a2e inset" }}
        data-testid="install-preview-android"
      >
        <div
          className="relative w-full h-full rounded-[1.75rem] overflow-hidden"
          style={{
            background: `linear-gradient(200deg, ${themeColor}18 0%, #0f172a 40%, #0a0a0b 100%)`,
          }}
        >
          {/* Punch-hole camera */}
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-2 h-2 bg-black rounded-full z-10 ring-1 ring-white/10" />
          {/* Status bar (Android-style) with themed accent */}
          <div className="absolute top-3 left-4 right-4 flex justify-between text-white text-[10px] font-mono-num tracking-wide pointer-events-none">
            <span style={{ color: themeColor }}>9:41</span>
            <div className="flex items-center gap-1">
              <Signal className="w-2.5 h-2.5" />
              <Wifi className="w-2.5 h-2.5" />
              <Battery className="w-3 h-3" />
            </div>
          </div>
          {/* Search pill */}
          <div className="absolute top-14 left-4 right-4 h-9 rounded-full bg-white/10 backdrop-blur-sm border border-white/10 flex items-center gap-2 px-3">
            <Search className="w-3 h-3 text-white/70" />
            <span className="text-[10px] text-white/70">Search apps</span>
            <div className="ml-auto w-5 h-5 rounded-full" style={{ background: themeColor }} />
          </div>
          {/* Home grid */}
          <div className="absolute inset-0 pt-28 pb-16 px-4">
            <div className="grid grid-cols-4 gap-x-2 gap-y-4">
              <AppIconAndroid icon={icon} label={label} themeColor={themeColor} highlight />
              <FakeIconAndroid name="Chrome" hue="#EF4444" glyph="G" />
              <FakeIconAndroid name="Gmail" hue="#EA4335" glyph="M" />
              <FakeIconAndroid name="Maps" hue="#22C55E" glyph="◎" />
              <FakeIconAndroid name="Photos" hue="#0EA5E9" glyph="◈" />
              <FakeIconAndroid name="Play" hue="#8B5CF6" glyph="▶" />
              <FakeIconAndroid name="YouTube" hue="#EF4444" glyph="▶" />
              <FakeIconAndroid name="Clock" hue="#0a0a0b" glyph="◷" />
            </div>
          </div>
          {/* Nav bar */}
          <div className="absolute bottom-0 left-0 right-0 h-8 bg-black/40 backdrop-blur-sm flex items-center justify-around">
            <div className="w-1 h-1 bg-white/60 rotate-45" />
            <div className="w-3 h-3 rounded-full border border-white/60" />
            <div className="w-3 h-3 border border-white/60" />
          </div>
        </div>
      </div>
    </div>
  );
}

function AppIconAndroid({ icon, label, themeColor, highlight }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <div
        className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center relative"
        style={{ background: themeColor, boxShadow: highlight ? `0 0 0 2px #fff, 0 0 0 4px ${themeColor}` : "none" }}
      >
        {icon ? (
          <img src={icon} alt="" className="w-full h-full object-cover" />
        ) : (
          <span className="text-black font-black text-base font-display">
            {(label || "?").slice(0, 1).toUpperCase()}
          </span>
        )}
      </div>
      <div className="text-[9px] text-white/90 max-w-[52px] truncate text-center leading-tight" data-testid="install-preview-android-label">
        {label}
      </div>
    </div>
  );
}

function FakeIconAndroid({ name, hue, glyph }) {
  return (
    <div className="flex flex-col items-center gap-1 opacity-45">
      <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm" style={{ background: hue }}>
        <span>{glyph}</span>
      </div>
      <div className="text-[9px] text-white/70 max-w-[52px] truncate text-center leading-tight">{name}</div>
    </div>
  );
}
